// Wire protocol between clients and a room host (Durable Object or the offline Web Worker).
// JSON messages; every inbound client message is validated with Zod. docs/07 §7.
import { z } from 'zod';
import type { AvailableActions, GameResult, GameSummary, LoggedEvent, PlayerView, RuleError, SeatId } from '@bcm/rules';

// 2: Resolutions and Annex rooms (docs/09): new command, choices, payment fields and settings.
export const PROTOCOL_VERSION = 2;

// ─── Room model ──────────────────────────────────────────────────────────────

export type RoomStatus = 'lobby' | 'playing' | 'ended';
export type TimerSetting = 'off' | 'relaxed' | 'standard' | 'blitz' | 'async';
export const TIMER_SECONDS: Record<TimerSetting, number | null> = {
  off: null,
  relaxed: 120,
  standard: 60,
  blitz: 30,
  async: 86_400,
};

export interface SeatInfo {
  seat: SeatId;
  name: string;
  kind: 'human' | 'bot';
  connected: boolean;
  /** Human seat currently being played by a bot (timeout / away). */
  away?: boolean;
}

export interface RoomSettings {
  maxPlayers: number;
  timer: TimerSetting;
  preset: 'v2' | 'v1';
  /** City Problem Resolutions and Annex rooms (P-17, P-18). Off: the classic rules. */
  resolutions: boolean;
}

export interface RoomSummary {
  code: string;
  status: RoomStatus;
  hostSeat: SeatId | null;
  seats: SeatInfo[];
  settings: RoomSettings;
  /** Epoch ms when the current turn times out, if a timer is running. */
  deadline: number | null;
  result: GameResult | null;
  /** The playtest game summary (docs/09 §10.1), once the match has ended. */
  report?: GameSummary | null;
}

export const QUICK_CHAT = [
  'Well filed!',
  'Objection!',
  'Per my last memo…',
  'Hmm…',
  'Approved.',
  'Denied!',
  'Good game',
] as const;

// ─── Client → host ───────────────────────────────────────────────────────────

const color = z.enum(['red', 'yellow', 'blue', 'green']);
const locationId = z.enum([
  'conjuration',
  'transmutation',
  'evocation',
  'archive',
  'divination',
  'enchantment',
  'procuration',
  'mana-conduit',
  'arcanium',
  'planning-office',
  'grand-auditor',
  'precedence',
  'exchange-bureau',
  'lost-property',
  'shredding-room',
  'stamp-pound',
  'complaints-desk',
  'mailroom',
]);
const permitBag = z.strictObject({
  red: z.number().int().min(0).max(99).optional(),
  yellow: z.number().int().min(0).max(99).optional(),
  blue: z.number().int().min(0).max(99).optional(),
  green: z.number().int().min(0).max(99).optional(),
});
const id = z.string().min(1).max(64);

const choice = z.union([
  z.strictObject({ identity: id, agenda: id, permits: permitBag.optional() }),
  z.strictObject({ permits: permitBag }),
  z.strictObject({ cards: z.array(id).max(20) }),
  z.strictObject({ card: id }),
  z.strictObject({ card: id, seat: z.number().int().min(0).max(4) }),
  z.strictObject({ problem: id, manaPermits: permitBag.optional() }),
  z.strictObject({ location: locationId }),
  z.strictObject({ target: z.number().int().min(0).max(4), give: color, take: color }),
  z.strictObject({ hand: id, arcanium: id }),
  z.strictObject({ decree: id }),
  z.strictObject({ seat: z.number().int().min(0).max(4) }),
  z.strictObject({ skip: z.literal(true) }),
  z.strictObject({ refresh: z.literal(true) }),
]);

export const commandSchema = z.discriminatedUnion('t', [
  z.strictObject({ t: z.literal('place'), location: locationId }),
  z.strictObject({
    t: z.literal('cast'),
    card: id,
    payment: z.strictObject({
      yellowAs: color.optional(),
      manaFor: color.optional(),
      discountOn: color.optional(),
      free: z.boolean().optional(),
      reduceBy: permitBag.optional(),
      manaPermits: permitBag.optional(),
    }),
  }),
  z.strictObject({ t: z.literal('playAction'), card: id }),
  z.strictObject({
    t: z.literal('activate'),
    ability: z.strictObject({ source: id, index: z.number().int().min(0).max(9) }),
  }),
  z.strictObject({ t: z.literal('decide'), decision: z.number().int().min(0), choice }),
  z.strictObject({ t: z.literal('endTurn'), skip: z.boolean().optional() }),
]);

const name = z.string().trim().min(1).max(24);

export const clientMessage = z.discriminatedUnion('t', [
  z.strictObject({
    t: z.literal('hello'),
    v: z.number().int(),
    /** Secret player token (random, stored client-side). Never broadcast. */
    token: z.string().min(16).max(128),
    name,
    lastSeq: z.number().int().min(0).default(0),
    spectate: z.boolean().optional(),
  }),
  z.strictObject({ t: z.literal('cmd'), clientSeq: z.number().int().min(0), cmd: commandSchema }),
  z.strictObject({ t: z.literal('undo'), clientSeq: z.number().int().min(0) }),
  z.strictObject({ t: z.literal('sync'), lastSeq: z.number().int().min(0) }),
  z.strictObject({
    t: z.literal('lobby'),
    op: z.discriminatedUnion('op', [
      z.strictObject({ op: z.literal('addBot') }),
      z.strictObject({ op: z.literal('removeSeat'), seat: z.number().int().min(0).max(4) }),
      z.strictObject({
        op: z.literal('settings'),
        settings: z.strictObject({
          maxPlayers: z.number().int().min(2).max(5).optional(),
          timer: z.enum(['off', 'relaxed', 'standard', 'blitz', 'async']).optional(),
          preset: z.enum(['v2', 'v1']).optional(),
          resolutions: z.boolean().optional(),
        }),
      }),
      z.strictObject({ op: z.literal('start') }),
      z.strictObject({ op: z.literal('rematch') }),
      z.strictObject({ op: z.literal('rename'), name }),
    ]),
  }),
  z.strictObject({ t: z.literal('chat'), phrase: z.number().int().min(0).max(QUICK_CHAT.length - 1) }),
  z.strictObject({ t: z.literal('ping') }),
]);

export type ClientMessage = z.infer<typeof clientMessage>;
export type LobbyOp = Extract<ClientMessage, { t: 'lobby' }>['op'];

// ─── Host → client ───────────────────────────────────────────────────────────

export type ServerError = RuleError | { code: 'BAD_MESSAGE' | 'NOT_SEATED' | 'NOT_HOST' | 'ROOM_FULL' | 'WRONG_STATUS' | 'VERSION' | 'RATE_LIMIT'; params?: Record<string, string | number> };

export type ServerMessage =
  | { t: 'welcome'; v: number; you: { seat: SeatId | null; playerId: string }; room: RoomSummary }
  | { t: 'room'; room: RoomSummary }
  /** Full resync: the viewer's current view plus redacted events after `fromSeq`. */
  | { t: 'sync'; seq: number; view: PlayerView; actions: AvailableActions | null; events: LoggedEvent[] }
  /** Incremental update after a command. */
  | { t: 'batch'; seq: number; events: LoggedEvent[]; view: PlayerView; actions: AvailableActions | null }
  /** Sent after the command's resulting batch, so the sender's state is already current. */
  | { t: 'ack'; clientSeq: number }
  | { t: 'err'; clientSeq?: number; error: ServerError }
  | { t: 'chat'; seat: SeatId; phrase: number }
  | { t: 'pong' };

export function parseClientMessage(raw: string): { ok: true; msg: ClientMessage } | { ok: false; error: ServerError } {
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return { ok: false, error: { code: 'BAD_MESSAGE', params: { reason: 'json' } } };
  }
  const r = clientMessage.safeParse(json);
  if (!r.success) return { ok: false, error: { code: 'BAD_MESSAGE', params: { reason: r.error.issues[0]?.message ?? 'invalid' } } };
  return { ok: true, msg: r.data };
}
